import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AttendanceChannel } from '../interfaces/attendance';
import { CompanyBlockDataService } from 'src/app/core/services/company-block-data.service';
import { SessionService } from 'src/app/core/services/session.service';
import { generateRequestTimeLogHeader } from 'src/app/core/interceptors/log.interceptor';

export enum BlockChannel {
  WHATSAPP = 'whatsapp',
  PHONE = 'phone',
  EMAIL = 'email',
}

const BLOCKABLE_CHANNELS: AttendanceChannel[] = [
  AttendanceChannel.WHATSAPP,
  AttendanceChannel.PHONE,
  AttendanceChannel.EMAIL,
];

export interface BlockTarget {
  channel: BlockChannel;
  identifier: string;
  autoHandled?: boolean;
}

interface BlockContactResponseStatus {
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BlockContactService {
  //TODO: remove localhost 
  private readonly baseUrl = environment.externalUrls.bff + '/block-contact';

  constructor(
    private httpClient: HttpClient,
    private companyBlockDataService: CompanyBlockDataService,
    private sessionService: SessionService
  ) {}

  isChannelBlockable(channel: AttendanceChannel): boolean {
    return BLOCKABLE_CHANNELS.includes(channel);
  }

  getBlockChannel(channel: AttendanceChannel): BlockChannel | null {
    switch (channel) {
      case AttendanceChannel.WHATSAPP:
        return BlockChannel.WHATSAPP;
      case AttendanceChannel.PHONE:
        return BlockChannel.PHONE;
      case AttendanceChannel.EMAIL:
        return BlockChannel.EMAIL;
      default:
        return null;
    }
  }

  isBlockedForAttendance(attendance: { currentContactChannel?: { channel?: AttendanceChannel }; currentCompany?: { id?: string }; contact?: { phone?: string; email?: string } }): boolean {
    const channel = attendance.currentContactChannel?.channel;
    if (!channel || !this.isChannelBlockable(channel)) return false;

    const companyId = attendance.currentCompany?.id;
    if (!companyId) return false;

    const phone = attendance.contact?.phone;
    const email = attendance.contact?.email;

    if (phone) {
      if (this.isBlocked(companyId, BlockChannel.WHATSAPP, phone)) return true;
      if (this.isBlocked(companyId, BlockChannel.PHONE, phone)) return true;
    }
    if (email) {
      if (this.isBlocked(companyId, BlockChannel.EMAIL, email)) return true;
    }

    return false;
  }

  isBlocked(
    companyId: string,
    channel: BlockChannel,
    identifier: string
  ): boolean {
    switch (channel) {
      case BlockChannel.WHATSAPP:
        return this.companyBlockDataService.isWhatsappBlocked(companyId, identifier);
      case BlockChannel.PHONE:
        return this.companyBlockDataService.isPhoneBlocked(companyId, identifier);
      case BlockChannel.EMAIL:
        return this.companyBlockDataService.isEmailBlocked(companyId, identifier);
      default:
        return false;
    }
  }

  blockContacts(
    companyId: string,
    targets: BlockTarget[],
  ) {
    return this.httpClient
      .post<BlockContactResponseStatus>(
        `${this.baseUrl}/${companyId}`,
        targets,
        {
          headers: generateRequestTimeLogHeader('block-contacts'),
        }
      )
      .pipe(
        tap(() => {
          targets.forEach(({ channel, identifier, autoHandled }) => {
            this.addBlockToLocalData(companyId, channel, identifier, autoHandled ?? false);
          });
        })
      );
  }

  unblockContacts(
    companyId: string,
    targets: BlockTarget[],
  ) {
    return this.httpClient
      .delete<BlockContactResponseStatus>(
        `${this.baseUrl}/${companyId}`,
        {
          body: targets,
          headers: generateRequestTimeLogHeader('unblock-contacts'),
        }
      )
      .pipe(
        tap(() => {
          targets.forEach(({ channel, identifier }) => {
            this.removeBlockFromLocalData(companyId, channel, identifier);
          });
        })
      );
  }

  private addBlockToLocalData(
    companyId: string,
    channel: BlockChannel,
    identifier: string,
    autoHandled: boolean
  ): void {
    const userName = this.sessionService.get()?.user?.name || '';
    const now = new Date().toISOString();

    switch (channel) {
      case BlockChannel.WHATSAPP:
        this.companyBlockDataService.addWhatsappBlock(companyId, {
          source: 'whatsapp',
          number: identifier,
          blockDate: now,
          userName,
        });
        break;
      case BlockChannel.PHONE:
        this.companyBlockDataService.addPhoneBlock(companyId, {
          id: identifier,
          date: now,
          autoHandled,
        });
        break;
      case BlockChannel.EMAIL:
        this.companyBlockDataService.addEmailBlock(companyId, {
          source: 'email',
          senderId: identifier,
          blockDate: now,
          userName,
        });
        break;
    }
  }

  private removeBlockFromLocalData(
    companyId: string,
    channel: BlockChannel,
    identifier: string
  ): void {
    switch (channel) {
      case BlockChannel.WHATSAPP:
        this.companyBlockDataService.removeWhatsappBlock(companyId, identifier);
        break;
      case BlockChannel.PHONE:
        this.companyBlockDataService.removePhoneBlock(companyId, identifier);
        break;
      case BlockChannel.EMAIL:
        this.companyBlockDataService.removeEmailBlock(companyId, identifier);
        break;
    }
  }
}
